/** Canonical incoming-invoice document type. App writes this value. */
export const INCOMING_INVOICE_DOCUMENT_TYPE = 'incoming_invoice' as const;

/** Legacy value still present in production until the follow-up backfill. */
export const LEGACY_INCOMING_INVOICE_DOCUMENT_TYPE = 'supplier_invoice' as const;

/** Lieferantengutschrift — not outgoing invoice_correction / credit_note. */
export const INCOMING_CREDIT_DOCUMENT_TYPE = 'incoming_invoice_correction' as const;

export const INCOMING_INVOICE_DOCUMENT_TYPES = [
  INCOMING_INVOICE_DOCUMENT_TYPE,
  LEGACY_INCOMING_INVOICE_DOCUMENT_TYPE,
  INCOMING_CREDIT_DOCUMENT_TYPE,
] as const;

export type IncomingInvoiceDocumentType = (typeof INCOMING_INVOICE_DOCUMENT_TYPES)[number];

export function isIncomingCreditDocumentType(documentType: string | null | undefined): boolean {
  return String(documentType ?? '').trim() === INCOMING_CREDIT_DOCUMENT_TYPE;
}

export function isIncomingInvoiceDocumentType(
  documentType: string | null | undefined,
): documentType is IncomingInvoiceDocumentType {
  const value = String(documentType ?? '').trim();
  return (
    value === INCOMING_INVOICE_DOCUMENT_TYPE
    || value === LEGACY_INCOMING_INVOICE_DOCUMENT_TYPE
    || value === INCOMING_CREDIT_DOCUMENT_TYPE
  );
}

export function incomingInvoiceDocumentTypes(): string[] {
  return [...INCOMING_INVOICE_DOCUMENT_TYPES];
}

export function incomingAmountSign(documentType: string | null | undefined): 1 | -1 {
  return isIncomingCreditDocumentType(documentType) ? -1 : 1;
}

/** Store incoming credits as negative totals; invoices as non-negative. */
export function normalizeIncomingSignedAmount(
  value: unknown,
  documentType: string | null | undefined,
): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  if (n === 0) return 0;
  return incomingAmountSign(documentType) * Math.abs(n);
}

export type IncomingExtractedDocumentKind = 'invoice' | 'credit_note';

/** ZUGFeRD/XRechnung credit note: UNTDID 1001 code 381 or UBL CreditNote. */
export function detectIncomingCreditFromXml(xmlText: string | null | undefined): boolean {
  const xml = String(xmlText ?? '');
  if (!xml) return false;
  if (/<(?:[\w.-]+:)?CreditNote[\s>]/i.test(xml)) return true;
  if (/(?:TypeCode|InvoiceTypeCode)[^>]*>\s*381\s*</i.test(xml)) return true;
  return false;
}

export function inferIncomingDocumentKind(params: {
  xmlText?: string | null;
  documentKind?: string | null;
  netAmount?: number | null;
  grossAmount?: number | null;
}): IncomingExtractedDocumentKind {
  const kind = String(params.documentKind ?? '').trim().toLowerCase();
  if (kind === 'credit_note' || kind === 'gutschrift' || kind === 'credit') return 'credit_note';
  if (detectIncomingCreditFromXml(params.xmlText)) return 'credit_note';
  if ((typeof params.netAmount === 'number' && params.netAmount < 0)
    || (typeof params.grossAmount === 'number' && params.grossAmount < 0)) {
    return 'credit_note';
  }
  return 'invoice';
}

export function incomingDocumentTypeFromKind(
  kind: IncomingExtractedDocumentKind,
): typeof INCOMING_INVOICE_DOCUMENT_TYPE | typeof INCOMING_CREDIT_DOCUMENT_TYPE {
  return kind === 'credit_note' ? INCOMING_CREDIT_DOCUMENT_TYPE : INCOMING_INVOICE_DOCUMENT_TYPE;
}

export function canonicalIncomingDocumentType(
  documentType: string | null | undefined,
): typeof INCOMING_INVOICE_DOCUMENT_TYPE | typeof INCOMING_CREDIT_DOCUMENT_TYPE {
  return isIncomingCreditDocumentType(documentType)
    ? INCOMING_CREDIT_DOCUMENT_TYPE
    : INCOMING_INVOICE_DOCUMENT_TYPE;
}

/** Select value for incoming invoices booked as company overhead, not a project. */
export const INVOICE_OVERHEAD_ALLOCATION = 'overhead';

export function invoiceAuftragSelectValue(row: {
  lead_id?: string | null;
  is_overhead?: boolean | null;
}): string {
  if (row.is_overhead) return INVOICE_OVERHEAD_ALLOCATION;
  return String(row.lead_id ?? '').trim();
}

export function invoiceAuftragPatchFromSelect(value: string | null | undefined): {
  lead_id: string | null;
  is_overhead: boolean;
} {
  const next = String(value ?? '').trim();
  if (next === INVOICE_OVERHEAD_ALLOCATION) return { lead_id: null, is_overhead: true };
  if (!next) return { lead_id: null, is_overhead: false };
  return { lead_id: next, is_overhead: false };
}

export function invoiceAuftragDisplayLabel(row: {
  lead_id?: string | null;
  lead_display_id?: string | null;
  is_overhead?: boolean | null;
}): string {
  if (row.is_overhead) return 'Gemeinkosten';
  const displayId = String(row.lead_display_id ?? '').trim();
  if (displayId) return displayId;
  const leadId = String(row.lead_id ?? '').trim();
  return leadId ? leadId.slice(0, 8) : '';
}

export function invoiceTabForDocument(row: {
  document_type?: string | null;
  direction?: string | null;
}): 'quotes' | 'outgoing' | 'incoming' {
  const documentType = String(row.document_type ?? '').trim();
  const direction = String(row.direction ?? '').trim();
  if (documentType === 'offer') return 'quotes';
  if (isIncomingInvoiceDocumentType(documentType) || isIncomingCreditDocumentType(documentType) || direction === 'incoming') {
    return 'incoming';
  }
  return 'outgoing';
}

function extractMailboxEmail(fromAddr: string): string {
  const raw = String(fromAddr || '').trim();
  const match = raw.match(/<([^>]+)>/);
  const candidate = (match ? match[1] : raw).trim().toLowerCase();
  if (candidate.includes('@') && candidate.includes('.')) return candidate;
  return '';
}

function isOutgoingFromMailbox(fromAddr: string, mailboxEmail: string): boolean {
  const acc = mailboxEmail.toLowerCase().trim();
  if (!acc) return false;
  const from = extractMailboxEmail(fromAddr);
  if (from && from === acc) return true;
  return String(fromAddr || '').toLowerCase().includes(acc);
}

/**
 * Combined send/receive invoice mailboxes: only ingest mail *to* the mailbox,
 * never PDFs we sent from it (outgoing customer invoices).
 */
export function shouldIngestIncomingInvoiceEmail(params: {
  fromAddr?: string | null;
  mailboxEmails: Array<string | null | undefined>;
}): boolean {
  const fromAddr = String(params.fromAddr ?? '');
  return !params.mailboxEmails.some((email) => isOutgoingFromMailbox(fromAddr, String(email ?? '')));
}

/** JPG, PNG, WebP und iPhone-HEIC/HEIF — per MIME oder, bei octet-stream, per Endung. */
export function isInvoiceImageType(params: { filename: string; mimeType?: string }): boolean {
  const mime = String(params.mimeType ?? '').toLowerCase().split(';')[0].trim();
  const name = params.filename.toLowerCase();
  return mime === 'image/jpeg'
    || mime === 'image/png'
    || mime === 'image/webp'
    || mime === 'image/heic'
    || mime === 'image/heif'
    || /\.(jpe?g|png|webp|heic|heif)$/.test(name);
}

const MIN_INVOICE_IMAGE_BYTES = 20_000;
const SIGNATURE_IMAGE_BASENAME =
  /^(image\d+|img\d+|logo|signature|signatur|sig|footer|header|banner|icon|spacer|pixel|facebook|linkedin|xing|instagram|twitter)$/i;

/**
 * Inline CID images, tiny logos, and Outlook `image001.png` files are signatures,
 * not invoice scans. Mailbox ingest only — manual upload is unchanged.
 */
export function isLikelyEmailSignatureImage(params: {
  filename: string;
  mimeType?: string;
  contentId?: string | null;
  size?: number | null;
  isInline?: boolean | null;
  contentDisposition?: string | null;
}): boolean {
  if (!isInvoiceImageType({ filename: params.filename, mimeType: params.mimeType })) return false;
  if (params.isInline === true) return true;
  if (String(params.contentDisposition ?? '').toLowerCase().includes('inline')) return true;
  if (String(params.contentId ?? '').trim()) return true;
  const size = params.size;
  if (typeof size === 'number' && Number.isFinite(size) && size > 0 && size < MIN_INVOICE_IMAGE_BYTES) {
    return true;
  }
  const base = params.filename.toLowerCase().replace(/\.[^.]+$/, '').trim();
  return SIGNATURE_IMAGE_BASENAME.test(base);
}

/**
 * Postfach-Import nimmt standardmäßig nur PDF und E-Rechnungs-XML: Bilder in Mails sind oft
 * Schadensfotos oder Signaturen und landeten sonst im GoBD-Archiv. Instanzen, die Belege als Foto
 * bekommen, schalten Bilder per `company_settings.incoming_invoice_mail_images` zu (`allowImages`);
 * Signaturbilder filtert dann `isLikelyEmailSignatureImage`.
 */
export function isIncomingInvoiceMailAttachment(params: {
  filename: string;
  mimeType?: string;
  allowImages?: boolean;
}): boolean {
  const mime = String(params.mimeType ?? '').toLowerCase().split(';')[0].trim();
  const name = params.filename.toLowerCase();
  if (params.allowImages && isInvoiceImageType(params)) return true;
  return mime === 'application/pdf'
    || name.endsWith('.pdf')
    || mime === 'application/xml'
    || mime === 'text/xml'
    || name.endsWith('.xml');
}

/** After a successful extract, the invoice is posted — overdue if the due date is already past. */
export function incomingInvoiceStatusAfterExtraction(dueDate?: string | null): 'open' | 'overdue' {
  const due = String(dueDate ?? '').trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(due) && due < new Date().toISOString().slice(0, 10)) return 'overdue';
  return 'open';
}

export const INCOMING_VENDOR_NUMBER_UNIQUE = 'uq_invoice_documents_incoming_vendor_number';

/** Must match the partial unique index: these statuses do not occupy the vendor number. */
export const INCOMING_VENDOR_NUMBER_INACTIVE_STATUSES = [
  'draft',
  'extraction_pending',
  'extraction_failed',
  'rejected',
  'cancelled',
] as const;

/** PostgREST `.not("status", "in", …)` argument; keep in sync with the unique index. */
export function incomingVendorNumberInactiveStatusInFilter(): string {
  return `(${INCOMING_VENDOR_NUMBER_INACTIVE_STATUSES.join(',')})`;
}

/**
 * vendor_invoice_number: gleiche Nummer beim selben Lieferanten (DB-Unique-Index).
 * same_file: identische Datei (SHA-256) schon als Eingangsbeleg erfasst.
 * same_number_party: ohne Lieferanten-Zuordnung gleiche Nummer + gleicher Name aus dem Beleg.
 */
export type IncomingInvoiceDuplicateReason = 'vendor_invoice_number' | 'same_file' | 'same_number_party';

const INCOMING_DUPLICATE_REASONS: readonly IncomingInvoiceDuplicateReason[] = [
  'vendor_invoice_number',
  'same_file',
  'same_number_party',
];

export type IncomingInvoiceDuplicateOf = {
  id: string;
  invoice_number: string | null;
  party_name: string | null;
  reason?: IncomingInvoiceDuplicateReason;
};

export function isIncomingVendorNumberUniqueError(error: unknown): boolean {
  const row = error as { code?: string; message?: string; details?: string; hint?: string } | null;
  const blob = `${row?.code ?? ''} ${row?.message ?? ''} ${row?.details ?? ''} ${row?.hint ?? ''}`;
  return blob.includes(INCOMING_VENDOR_NUMBER_UNIQUE);
}

export function parseIncomingInvoiceDuplicateOf(extractionJson: unknown): IncomingInvoiceDuplicateOf | null {
  if (!extractionJson || typeof extractionJson !== 'object' || Array.isArray(extractionJson)) return null;
  const raw = (extractionJson as { duplicate?: unknown }).duplicate;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const dup = raw as Record<string, unknown>;
  const reason = (String(dup.reason ?? '').trim() || 'vendor_invoice_number') as IncomingInvoiceDuplicateReason;
  if (!INCOMING_DUPLICATE_REASONS.includes(reason)) return null;
  const id = String(dup.existing_invoice_id ?? dup.id ?? '').trim();
  const invoiceNumber = String(dup.existing_invoice_number ?? dup.invoice_number ?? '').trim();
  const partyName = String(dup.existing_party_name ?? dup.party_name ?? '').trim();
  if (!id && !invoiceNumber) return null;
  return {
    id,
    invoice_number: invoiceNumber || null,
    party_name: partyName || null,
    reason,
  };
}

export function incomingInvoiceDuplicatePayload(duplicateOf: IncomingInvoiceDuplicateOf): Record<string, unknown> {
  return {
    reason: duplicateOf.reason ?? 'vendor_invoice_number',
    existing_invoice_id: duplicateOf.id || null,
    existing_invoice_number: duplicateOf.invoice_number,
    existing_party_name: duplicateOf.party_name,
  };
}

export function stripIncomingInvoiceDuplicateFlag(extractionJson: unknown): unknown {
  if (!extractionJson || typeof extractionJson !== 'object' || Array.isArray(extractionJson)) return extractionJson;
  if (!('duplicate' in extractionJson)) return extractionJson;
  const { duplicate: _duplicate, ...rest } = extractionJson as Record<string, unknown>;
  return rest;
}

export function incomingVendorNumberDuplicateMessage(duplicateOf?: IncomingInvoiceDuplicateOf | null): string {
  const number = String(duplicateOf?.invoice_number ?? '').trim();
  if (duplicateOf?.reason === 'same_file') {
    return number
      ? `Diese Datei ist bereits als Eingangsrechnung ${number} erfasst.`
      : 'Diese Datei ist bereits als Eingangsrechnung erfasst.';
  }
  if (duplicateOf?.reason === 'same_number_party') {
    const party = String(duplicateOf.party_name ?? '').trim();
    const from = party ? ` von „${party}“` : '';
    return number
      ? `Rechnungsnummer ${number}${from} existiert bereits.`
      : `Diese Rechnungsnummer${from} existiert bereits.`;
  }
  if (number) return `Rechnungsnummer ${number} existiert bereits für diesen Lieferanten.`;
  return 'Diese Rechnungsnummer existiert bereits für diesen Lieferanten.';
}

function toNetAmount(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export function sumInvoiceNet(rows: Array<{ net_amount?: unknown } | null | undefined>): number {
  const sum = rows.reduce((acc, row) => acc + toNetAmount(row?.net_amount), 0);
  return Math.round(sum * 100) / 100;
}

/** Rows without a usable net amount — left out of the net sums instead of guessed. */
export function countMissingNet(rows: Array<{ net_amount?: unknown } | null | undefined>): number {
  return rows.filter((row) => {
    const value = row?.net_amount;
    return value == null || value === '' || !Number.isFinite(Number(value));
  }).length;
}

/** Project cost KPIs from the same rows that appear in the lead Kosten list. */
export function leadCostTotals(opts: {
  incoming: Array<{ net_amount?: unknown }>;
  outgoing: Array<{ net_amount?: unknown }>;
}): {
  outgoing_net: number;
  incoming_net: number;
  gross_margin: number;
  outgoing_missing_net: number;
  incoming_missing_net: number;
} {
  const incoming_net = sumInvoiceNet(opts.incoming);
  const outgoing_net = sumInvoiceNet(opts.outgoing);
  return {
    outgoing_net,
    incoming_net,
    gross_margin: Math.round((outgoing_net - incoming_net) * 100) / 100,
    outgoing_missing_net: countMissingNet(opts.outgoing),
    incoming_missing_net: countMissingNet(opts.incoming),
  };
}

export const INCOMING_INVOICE_LABEL = 'Eingangsrechnung';
export const INCOMING_CREDIT_LABEL = 'Gutschrift';
export const INCOMING_CREDIT_SEARCH_LABEL = 'Lieferantengutschrift';

export function incomingDocumentTypeLabel(documentType: string | null | undefined): string {
  return isIncomingCreditDocumentType(documentType) ? INCOMING_CREDIT_LABEL : INCOMING_INVOICE_LABEL;
}

export function incomingDocumentTypeSearchLabel(documentType: string | null | undefined): string {
  return isIncomingCreditDocumentType(documentType) ? INCOMING_CREDIT_SEARCH_LABEL : INCOMING_INVOICE_LABEL;
}

export type IncomingExtractionMethod = 'zugferd' | 'xrechnung' | 'ai' | 'manual';

export function isIncomingExtractionMethod(
  value: string | null | undefined,
): value is IncomingExtractionMethod {
  return value === 'zugferd' || value === 'xrechnung' || value === 'ai' || value === 'manual';
}
